/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useEffect } from 'react';
// material
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@insticator/insticator-ui';
import Space from 'material/space/space';
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ConfigV2Controller } from 'material/expandablesection/v2';
import { ReactComponent as BoardActivity } from 'assets/icons/board-activity.svg'
// content
import ProductGroup from './productgroup/productgroup'
// styles
import injectSheet from 'react-jss'
import styles from './pagesetup_styles'
import { setTempProductManagerConfig } from 'state/slices/productmanager';


/* ========== ~~~~~~~~~~ PRODUCT MANAGER PAGE: PAGE SETUP ~~~~~~~~~~ ========== */
const PageSetup = injectSheet(styles)((props) => {

  const dispatch = useDispatch()
  const { tempProductManagerConfig, productManagerConfig }  = useSelector(state => state.productManager)
  const {
    account: {
      accountProducts: approvedAccountProducts
    }
  } = useSelector((state) => state)

  const { sites } = useSelector((state) => state.account);
  const { siteUUID } = useSelector(state => state.session)

  const [isOpened, setIsOpened] = useState(false)
 
  const handleToggle = () => setIsOpened(!isOpened)

  const handleAddGroup = useCallback((e) => {
    e.stopPropagation()
    const commentingEnabled = approvedAccountProducts.some(product => product === 'COMMENTING_V2')
    const trendingNowEnabled = approvedAccountProducts.some(product => product === 'TRENDING_NOW_V2')

    const defaultProductGroup =  {
      groupId: null,
      groupOrder: null,
      draft: {
        groupEnabled: true,
        infiniteScrollEnabled: false,
        label: "New Group",
        showCommenting: commentingEnabled ? true : false,
        commentingOrder: 1,
        showTrendingNow: trendingNowEnabled ? true : false, 
        trendingNowOrder: 2,
        showArticleSentiment: false,
        articleSentimentOrder: 3,
        dynamicPlacement: true,
        selectorMode: "CSS", 
        selector: "",
        urlFilter: ""
      },
      current: {
        groupEnabled: false,
        infiniteScrollEnabled: false,
        label: "New Group",
        showCommenting: commentingEnabled ? true : false,
        commentingOrder: 1,
        showTrendingNow: trendingNowEnabled ? true : false, 
        trendingNowOrder: 2,
        showArticleSentiment: false,
        articleSentimentOrder: 3,
        dynamicPlacement: true,
        selectorMode: "CSS",
        selector: "",
        urlFilter: ""
      }
    }
    dispatch(
      setTempProductManagerConfig({config: {
        ...tempProductManagerConfig,
        groupConfigList: [defaultProductGroup, ...tempProductManagerConfig.groupConfigList]
      }, changes: 'groupConfigList'})
    )
  }, [dispatch, tempProductManagerConfig])

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      actionButtons={
        <Button 
          name={'creategroup'} 
          type={'action'}
          variant={'none'} 
          color={'light'} 
          text={'Add Product Group'} 
          handleClick={handleAddGroup} 
        />
      }
      handleToggle={handleToggle}
      icon={<BoardActivity />}
      title='Page Setup'
    />
  );

  const handleProductGroupChange = useCallback((index, property, value, selectedMode = 'draft') => {
    const updatedGroups = JSON.parse(JSON.stringify(tempProductManagerConfig.groupConfigList))
    updatedGroups[index][selectedMode] = {...updatedGroups[index][selectedMode], [property]: value}
    // Set to hardcode when infinite scroll is enabled
    if(property === 'infiniteScrollEnabled' && value === true) {
      updatedGroups[index][selectedMode]['dynamicPlacement'] = false 
    }

    if(property === 'infiniteScrollEnabled' && value === false) {
      updatedGroups[index][selectedMode]['dynamicPlacement'] = false 
    }

    if(property === 'label') {
      updatedGroups[index]['saveDraftText'] = 'Save Draft'
    } else {
      updatedGroups[index]['saveDraftText'] = 'Save for Testing'
    }

    // if there's no product selected disable group 
    // TODO: V2 For trending and article sentiment
    // if(property === 'showCommenting' && value === false) {

    //   updatedGroups[index][selectedMode]['groupEnabled'] = false 
    // }

    if(updatedGroups[index][selectedMode]['showCommenting'] === false && updatedGroups[index][selectedMode]['showTrendingNow'] === false && property !== 'groupEnabled') {
      updatedGroups[index][selectedMode]['groupEnabled'] = false 
    }

    if(property === 'groupEnabled' && value === true) {
      updatedGroups[index][selectedMode]['showCommenting'] = true
      updatedGroups[index][selectedMode]['showTrendingNow'] = true
    } 

    const newConfig = {...tempProductManagerConfig, groupConfigList: updatedGroups}
    dispatch(setTempProductManagerConfig({config: newConfig, changes: property === 'label' ? 'label' : 'groupConfigList'}))
  }, [dispatch, tempProductManagerConfig])

  const handleProductGroupPropertiesChange = useCallback((index, newPropertyValues, selectedMode = 'draft') => {
    // For multiple properties change at once
    const updatedGroups = JSON.parse(JSON.stringify(tempProductManagerConfig.groupConfigList))
    updatedGroups[index][selectedMode] = {...updatedGroups[index][selectedMode],...newPropertyValues}
    if(updatedGroups[index][selectedMode]['showCommenting'] === false && updatedGroups[index][selectedMode]['showTrendingNow'] === false) {
      updatedGroups[index][selectedMode]['groupEnabled'] = false 
    }
    const newConfig = {...tempProductManagerConfig, groupConfigList: updatedGroups}
    dispatch(setTempProductManagerConfig({config: newConfig, changes: 'groupConfigList'}))
  }, [dispatch, tempProductManagerConfig])

  const handleProductGroupRemove = useCallback((index) => {
    const updatedGroups = tempProductManagerConfig.groupConfigList.filter((group, i) => i !== index)
    const removedId = tempProductManagerConfig.groupConfigList[index].groupId
    
    // if existing group, add it on the removedGroupIds array
    let removedGroupIds = Array.isArray(tempProductManagerConfig.removedGroupIds) ? [...tempProductManagerConfig.removedGroupIds] : []
    if(removedId) removedGroupIds = [...removedGroupIds, removedId]
  
    dispatch(setTempProductManagerConfig({config: {...tempProductManagerConfig, groupConfigList: updatedGroups, removedGroupIds }, changes: 'groupConfigList'}))
  },[dispatch, tempProductManagerConfig])

  const renderGroup = () => {
    const isGroupEmpty = tempProductManagerConfig.groupConfigList.length === 0
    return !isGroupEmpty ? tempProductManagerConfig.groupConfigList.map((group, index) => {
    const isDraftChanged = !(JSON.stringify(group.draft) === JSON.stringify(productManagerConfig.groupConfigList[index]?.draft || null));

      return (
        <ProductGroup 
          index={index}
          group={group}
          siteURL={sites[siteUUID] || ''}
          onRemove={() => handleProductGroupRemove(index)} 
          onChange={(property, value, selectedMode) => {
            handleProductGroupChange(index, property, value, selectedMode)
          }} 
          onFieldsChange={(properties, selectedMode) => {
            handleProductGroupPropertiesChange(index, properties, selectedMode)
          }} 
          isDraftChanged={isDraftChanged}
          saveDraftText={group.saveDraftText || 'Save for Testing'}
        />
      )
    }) : <Space alignItems={'center'} justifyContent={'center'} style={{height: '167px'}}>No product groups</Space>
  }

  return (
    <ExpandableSection
      className={`expandablesectionv2 pagesetup ${props.classes.pagesetup}`}
      controller={controller}
      isOpened={isOpened}
      disableSave={false}
    >
      <Space gap={16} direction={'column'}>
        {renderGroup()}
      </Space>
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default PageSetup;